/**
 * The WEEKLY canvas, view-shaped: seven columns, every label written and every number formatted.
 *
 * `entities/plan-draft` answers what the plan IS — which days, which exercises, how many counting
 * sets, how the muscle points fall. This module answers what the grid SAYS. The split is what lets
 * the entity be tested without a locale and the components be written without a rule in them.
 *
 * Three things it no longer holds, and each moved because the sequence tape needed the same thing:
 * the primitives (`builderCopy`), one workout card (`dayCardView`), and everything around the canvas
 * (`builderChrome`). What is left here is the part that is genuinely about a WEEK — the seven fixed
 * columns, the weekday a card is moved to, and the banner naming the days no column could hold.
 *
 * **A component below this reads only the view model.** No `t`, no `Intl`, no arithmetic, no
 * `draft.phases[i]`. If a component is deciding something, it belongs here.
 */

import type {
  MuscleLoadBreakdown,
  PhaseSummary,
  PlanDraft,
  WeekProjection,
} from '~/entities/plan-draft';

import { weekdayTargets } from './dayMove';
import { KEY, weekdayLabels, weekdayShortLabel, type Translate } from './builderCopy';
import {
  dayDisplayName,
  toDayCardView,
  type DayCardView,
  type LookUpExercise,
} from './dayCardView';
import {
  toBuilderChrome,
  type BuilderChromeView,
  type DayMoveTargetView,
  type PatternBarView,
} from './builderChrome';

export interface WeekdayColumnView {
  readonly key: string;
  /** 1 (Monday) – 7 (Sunday). The coordinate `addDay` takes; the label is for reading. */
  readonly weekday: number;
  /**
   * "poniedziałek" — the full name, and it is what the column says to assistive technology.
   *
   * It is no longer what the column DRAWS: the 07.09 programator mock heads each column with two
   * letters, so `weekdayShortLabel` is the visible one and this stands beside it, visually hidden.
   */
  readonly weekdayLabel: string;
  /** "PN" — the two letters the mock draws. The component upper-cases it, as the canon does. */
  readonly weekdayShortLabel: string;
  /** "D1". */
  readonly dayNumber: string;
  /**
   * "~62 min" — the day's authored estimate, printed under the weekday name.
   *
   * **`null` when the session carries none, and the column then prints NOTHING.** Not "~0 min" and
   * not a dash: duration is only ever the trainer's own `EstimatedDurationMinutes` and nothing in
   * either client derives one from a set count. The 07.09 handoff moved this line out of the card's
   * header, where it used to ride on `meta`, and up into the column above it.
   */
  readonly duration: string | null;
  /** Saturday and Sunday, which the canon renders a half-step quieter. */
  readonly isWeekend: boolean;
  readonly day: DayCardView | null;
  /** "Przerwa" — what an unprogrammed weekday says. */
  readonly emptyLabel: string;
}

export interface WeekViewModel {
  readonly chrome: BuilderChromeView;
  /**
   * The pattern band, between the phase strip and the grid — "this one week IS the pattern, and it
   * runs ×3".
   *
   * ── It states the thing the week grid never said out loud ──────────────────────────────────
   * A plan on the weekly canvas draws ONE week and repeats it for the length of the phase. Nothing
   * on this screen said so: the phase tab carried "tyg. 1–4" and the grid below it drew a single
   * week, and a trainer reading the two had to know already that the second was the pattern for the
   * first. The 07.09 programator mock puts it in words in its own 44px band, and the ± there is the
   * SAME operation the phase editor's length stepper dispatches — `setPhaseCycles` — so there is one
   * way to change a phase's length and two places to reach it.
   *
   * The VIEW is `PatternBarView`, shared with the sequence canvas since 13 September 2026: both
   * mocks draw the same band and differ only in what stands inside the pill. What is week-specific
   * is that `repeat` is non-null here and `note` is null.
   */
  readonly pattern: PatternBarView;
  readonly columns: readonly WeekdayColumnView[];
  /** Named work that no weekday column could hold. `null` when there is none. */
  readonly overflow: string | null;
}

export interface WeekViewModelInput {
  readonly draft: PlanDraft;
  readonly projection: WeekProjection;
  readonly phases: readonly PhaseSummary[];
  readonly load: MuscleLoadBreakdown;
  /** What the exercise catalogue knows about one movement. See `LookUpExercise`. */
  readonly lookUpExercise: LookUpExercise;
  /** Whether that catalogue read is still open — the volume rail's only wait. See `volumeStatus`. */
  readonly isAttributionPending: boolean;
  readonly t: Translate;
  /** The active i18next language, for `Intl`. */
  readonly locale: string;
}

/**
 * Build the weekly canvas and the chrome around it.
 *
 * `load.days` is positional against the projection's PROGRAMMED days, in the order the caller
 * flattened them — so the caller must flatten the columns the same way it built the load. It does:
 * `usePlanBuilder` derives both from `projection.columns` in one pass, which is what keeps the day
 * a bar belongs to and the day it is drawn under the same day.
 */
/**
 * Work the seven columns could not hold, worded — or `null` when every day found a column.
 *
 * ── It states the CAUSE, and it used to state the wrong one ──────────────────────────────────
 * Two different things put a day here, and only one of them was ever named: "nie ma przypisanego
 * dnia tygodnia". The other is a day whose weekday is already TAKEN by a sibling — the more common
 * of the two, since the phone authors that shape and the calendar's day editor can add a second row
 * to a weekday that already has one. So the banner reported a cause that was true of at most one of
 * the days it was counting. (The mode flip that used to strand a whole phase at once is gone — R1
 * fixed the mode at creation on 16 August 2026 — but neither cause went with it.)
 *
 * ── And it names the way back ────────────────────────────────────────────────────────────────
 * An overflowed day renders no card, no menu and no grip — there is no column for it to sit in — so
 * nothing on this screen can give it a weekday. A banner reporting an unreachable state without
 * saying how to leave it is a dead end.
 *
 * There are two ways out now, and the recovery sentence used to name only the first. Undo still
 * answers the case this banner was written for: the flip to the weekly grid is one patch, so one
 * Cmd+Z restores the whole thing. It does NOT answer a duplicate weekday that arrived from the
 * SERVER, which the calendar's one-off add (`addDayOneTime`, owner decision of 15 August 2026) can
 * now put there — a second training on one weekday, running in one cycle. That day is reachable and
 * editable on the calendar, in the week it runs in, and telling the trainer to press Cmd+Z would be
 * advice for a change they did not make in this tab. Both are named, and neither is a per-day
 * reason on the card — nothing renders one, and a field carried for a sentence three elements away
 * is a field that goes stale.
 *
 * Built as clauses rather than one string per combination: a plan can carry both causes at once,
 * and three copies of "and their volume is still counted" is what a per-combination key gives you.
 */
function overflowBanner(t: Translate, projection: WeekProjection): string | null {
  const { noWeekday, duplicateWeekday } = projection.overflowCauses;
  if (noWeekday === 0 && duplicateWeekday === 0) return null;

  const causes: string[] = [];
  if (noWeekday > 0) causes.push(t(`${KEY}.overflow`, { count: noWeekday }));
  if (duplicateWeekday > 0) causes.push(t(`${KEY}.overflowTaken`, { count: duplicateWeekday }));

  return [...causes, t(`${KEY}.overflowVolume`), t(`${KEY}.overflowRecovery`)].join(' ');
}

export function toWeekViewModel({
  draft,
  projection,
  phases,
  load,
  lookUpExercise,
  isAttributionPending,
  t,
  locale,
}: WeekViewModelInput): WeekViewModel {
  const weekdays = weekdayLabels(locale);

  // Each programmed session's 1-based position in the phase, by weekday. The week places cards by
  // weekday, but a training is NAMED by its position — the same number a sequence card's move menu
  // addresses it by — so an unnamed Friday card is "Dzień 3" (the third training), not "Dzień 5".
  const positions = new Map<number, number>();
  projection.columns.forEach((column) => {
    if (column.day !== null) positions.set(column.weekday, positions.size + 1);
  });

  const nameOf = (day: { readonly title: string | null }, weekday: number): string =>
    dayDisplayName(t, day.title, positions.get(weekday) ?? 1);

  // Which weekday each programmed session stands on — read once and handed to every card, because
  // a card's move menu has to name what it would swap with and cannot see its siblings.
  const occupants = new Map<number, string>(
    projection.columns.flatMap((column) =>
      column.day === null ? [] : [[column.weekday, nameOf(column.day, column.weekday)] as const],
    ),
  );

  // The load array covers the programmed days only, in column order; an empty column consumes no
  // entry. Walking one cursor is what keeps the two in step without either side carrying an index.
  let programmed = 0;

  const columns = projection.columns.map((column): WeekdayColumnView => {
    const dayLoad = column.day === null ? undefined : load.days[programmed];
    if (column.day !== null) programmed += 1;
    return {
      key: `weekday-${String(column.weekday)}`,
      weekday: column.weekday,
      weekdayLabel: weekdays[column.weekday - 1] ?? '',
      weekdayShortLabel: weekdayShortLabel(t, column.weekday),
      dayNumber: t(`${KEY}.dayNumber`, { number: column.weekday }),
      duration:
        column.day?.durationMinutes == null
          ? null
          : t(`${KEY}.dayDuration`, { minutes: column.day.durationMinutes }),
      isWeekend: column.weekday >= 6,
      day:
        column.day === null
          ? null
          : toDayCardView(
              t,
              locale,
              column.day,
              dayLoad,
              lookUpExercise,
              {
              index: column.weekday - 1,
              position: positions.get(column.weekday) ?? 1,
              menuGroupLabel: t(`${KEY}.dnd.moveToWeekday`),
              liftedLabel: (name) =>
                t(`${KEY}.dnd.dayLifted`, {
                  name,
                  weekday: weekdays[column.weekday - 1] ?? '',
                  position: column.weekday,
                  total: 7,
                }),
              targets: weekdayTargets(t, {
                weekdayLabels: weekdays,
                currentWeekday: column.weekday,
                // The card's OWN weekday is excluded from the occupant map, so its own entry reads
                // "obecny dzień" rather than offering to swap the session with itself.
                occupants: new Map([...occupants].filter(([weekday]) => weekday !== column.weekday)),
              }),
            },
            isAttributionPending,
          ),
      emptyLabel: t(`${KEY}.restDay`),
    };
  });

  const dayTargets: DayMoveTargetView[] = projection.columns.flatMap((column) =>
    column.day === null
      ? []
      : [
          {
            slotToken: column.day.slotToken,
            weekday: column.weekday,
            label: t(`${KEY}.dnd.dayTarget`, {
              weekday: weekdays[column.weekday - 1] ?? '',
              name: nameOf(column.day, column.weekday),
            }),
            shortLabel: weekdays[column.weekday - 1] ?? '',
            blockCount: column.day.blocks.length,
          },
        ],
  );

  const chrome = toBuilderChrome({
    draft,
    phases,
    phase: projection.phase,
    load: load.week,
    // The PLAN's span. The sequence canvas states the same fact in the same slot — see `bandTotal`.
    bandTotal: t(`${KEY}.bandTotal`, {
      cycles: t(`${KEY}.weeks`, {
        count: phases.reduce((sum, each) => sum + each.cycleCount, 0),
      }),
      phases: t(`${KEY}.phases`, { count: phases.length }),
    }),
    dayTargets,
    // A week's load already IS a week's, so the rail has nothing to convert and says "serie
    // tygodniowo". Only the sequence canvas hands over a cycle length.
    cycleLengthDays: null,
    isAttributionPending,
    t,
    locale,
  });

  return {
    chrome,
    pattern: toRepeatingWeek(t, chrome, projection.phase.cycleCount, columns),
    columns,
    overflow: overflowBanner(t, projection),
  };
}

/**
 * The repeating-week band, worded.
 *
 * Everything it says is read off something that already exists rather than re-derived: the range is
 * the phase tab's own string, the two steps are the phase editor's own `length`, and the two counts
 * are the columns this function was just handed. That is what keeps the band from becoming a second
 * opinion about the phase it describes — the failure this file's header warns about one level up.
 */
function toRepeatingWeek(
  t: Translate,
  chrome: BuilderChromeView,
  cycles: number,
  columns: readonly WeekdayColumnView[],
): PatternBarView {
  const trainingDays = columns.filter((column) => column.day !== null).length;
  const length = chrome.phaseEditor?.length ?? null;

  return {
    title: t(`${KEY}.repeatingWeekTitle`),
    repeat: {
      repeatsLabel: t(`${KEY}.repeatWeek.repeats`),
      count: t(`${KEY}.repeatWeek.count`, { count: cycles }),
      countLabel: t(`${KEY}.repeatWeek.countLabel`, { count: cycles }),
      // The tab's own string, not a second format of the same span. A phase whose tab has scrolled
      // out of the strip is exactly when this band is the only place the range is readable.
      range: chrome.phases.find((phase) => phase.isActive)?.range ?? '',
      decrease: length?.decrease ?? null,
      increase: length?.increase ?? null,
    },
    // The week repeats a COUNT, so it has no static sentence to put in the pill's place.
    note: null,
    summary: t(`${KEY}.repeatWeek.summary`, {
      days: t(`${KEY}.trainingDays`, { count: trainingDays }),
      rests: t(`${KEY}.restDays`, { count: columns.length - trainingDays }),
    }),
  };
}
